import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useUiPrefs from "../hooks/useUiPrefs";
import { CARD_BLOCKS, cardColumns, normalizeCardPrefs, toggleIn } from "../components/employees/employeeCardLayout";
import EmployeeCardViewPanel from "../components/employees/EmployeeCardViewPanel";
import EmployeeMergeDialog from "../components/employees/EmployeeMergeDialog";
import EmployeeDeleteDialog from "../components/employees/EmployeeDeleteDialog";
import { getMergeRights } from "../services/employeeMergeApi";
import MainLayout from "../layout/MainLayout";
import ContactEditor from "../components/employees/ContactEditor";
import FamilySection from "../components/employees/FamilySection";
import EmployeeErpAccountForm from "../components/employees/EmployeeErpAccountForm";
import EmployeeDocumentDetailsEditor from "../components/employees/EmployeeDocumentDetailsEditor";
import EmployeePhotosTab from "../components/employees/EmployeePhotosTab";
import EmployeeAssetsTab from "../components/employees/EmployeeAssetsTab";
import { regenerateCandidateContractsFromRequisites } from "../services/candidateDocumentsApi";
import {
    createDepartmentContact,
    createEmployeeErpAccount,
    createEmployeeContact,
    deactivateContact,
    getEmployee,
    getEmployeeContacts,
    getEmployeeDepartmentContacts,
    getEmployeeDocumentDetails,
    getEmployeeOverview,
    addEmployeeResume,
    deleteEmployeeResume,
    selectEmployeeDepartmentContact,
    updateContact,
    updateEmployee,
    updateEmployeeDocumentDetails,
} from "../services/hrOpsApi";
import { useAlertContext } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { formatDateRu, formatDateTimeRu } from "../utils/dateFormat";
import { DEPARTMENTS } from "../config/api";
import { formatCompensation, parseCompensationInput } from "../utils/compensation";
import { adaptationText, ageOf, birthWithNumerology, formatTenure } from "../components/employees/employeeColumns";

/**
 * Карточка сотрудника — плотная шапка и вкладки (Чат 4, правка №15, MR 2). [Claude] 2026-11-02
 * Доработка 5 (Света 04.11): вместо вкладок — одна страница из блоков; порядок, колонки, скрытие
 * и сворачивание каждый настраивает у себя («Вид карточки»). Владелец может объединить карточки
 * с выбором полей и удалить карточку насовсем. [Claude] 2026-11-04
 * Обзор · Резюме · Найм · Адаптация · Документы · Фото · Семья · Контакты · Техника · Подарки.
 * Оплата приходит скрытой (salary_hidden), если у пользователя нет права «ЗП сотрудников».
 */
const HR_ROLES = ["superadmin", "admin", "manager", "senior_manager", "hr", "owner", "dev", "tech_admin", "director"];

// Блоки карточки и их порядок по умолчанию — components/employees/employeeCardLayout.js (доработка 5).

const dash = (value) => (value === null || value === undefined || value === "" ? "—" : value);

// Карточка: плитки полей без пустот между колонками, размер — между старой и первой новой версией (правка №15, MR 4).
const FIELDS_GRID = "grid gap-2 grid-cols-[repeat(auto-fill,minmax(200px,1fr))]";
const HEADER_LINK = "rounded-xl border border-white/25 bg-white/10 px-3 py-1.5 text-sm font-semibold text-white hover:bg-white/20";

const hostOf = (url) => {
    try {
        return new URL(url).hostname.replace(/^www\./, "");
    } catch {
        return url;
    }
};

function Field({ label, children, wide = false }) {
    return <div className={`rounded-xl bg-slate-50 px-3 py-2 ${wide ? "sm:col-span-2" : ""}`}>
        <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
        <dd className="mt-0.5 break-words text-[15px] font-semibold text-slate-900">{children}</dd>
    </div>;
}

function Panel({ title, children, actions }) {
    return <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
        {title || actions ? <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            {title ? <h2 className="text-lg font-bold text-slate-900">{title}</h2> : <span />}
            {actions}
        </div> : null}
        {children}
    </section>;
}

function LinkCard({ to, href, title, text, testId }) {
    const body = <>
        <div className="text-sm font-semibold text-slate-900">{title} →</div>
        {text ? <div className="mt-0.5 text-xs text-slate-500">{text}</div> : null}
    </>;
    const cls = "block rounded-xl border border-slate-200 px-3 py-2.5 hover:border-amber-300 hover:bg-amber-50/50";
    return href
        ? <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testId}>{body}</a>
        : <Link to={to} className={cls} data-testid={testId}>{body}</Link>;
}

function Placeholder({ text, testId }) {
    return <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500" data-testid={testId}>{text}</div>;
}

export default function EmployeeDetailPage() {
    const { employeeId } = useParams();
    const { showAlert } = useAlertContext();
    const { user } = useAuth();
    const canManage = HR_ROLES.includes(String(user?.role || "").toLowerCase());
    const { prefs: cardPrefsRaw, update: updateCardPrefs } = useUiPrefs("employee_card", normalizeCardPrefs);
    const cardPrefs = normalizeCardPrefs(cardPrefsRaw);
    const [showCardView, setShowCardView] = useState(false);
    const [rights, setRights] = useState({ can_merge: false, can_delete: false });
    const [dialog, setDialog] = useState(null); // "merge" | "delete"
    const [employee, setEmployee] = useState(null);
    const [overview, setOverview] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [departmentContacts, setDepartmentContacts] = useState({ department_id: null, selected_contact_id: null, contacts: [] });
    const [workStartDate, setWorkStartDate] = useState("");
    const [savingDate, setSavingDate] = useState(false);
    const [editingProfile, setEditingProfile] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);
    const [showErpForm, setShowErpForm] = useState(false);
    const [creatingErp, setCreatingErp] = useState(false);
    const [documentDetailsOpen, setDocumentDetailsOpen] = useState(false);
    const [documentDetailsLoading, setDocumentDetailsLoading] = useState(false);
    const [documentDetails, setDocumentDetails] = useState(null);
    const [editingDocumentDetails, setEditingDocumentDetails] = useState(false);
    const [regeneratingContracts, setRegeneratingContracts] = useState(false);
    const [resumeUrl, setResumeUrl] = useState("");
    const [resumeTitle, setResumeTitle] = useState("");
    const [resumes, setResumes] = useState([]);
    const [confirmResume, setConfirmResume] = useState(null);
    const [savingResume, setSavingResume] = useState(false);
    const [profileForm, setProfileForm] = useState({ department: "", position: "", phone_number: "", email: "", telegram_username: "", birth_date: "", compensation: "" });

    const compensationText = (emp) => emp?.salary_terms || (emp?.salary != null ? String(emp.salary) : "");

    const fillForm = (loaded) => setProfileForm({
        department: loaded?.department || "",
        position: loaded?.position || "",
        phone_number: loaded?.phone_number || "",
        email: loaded?.email || "",
        telegram_username: loaded?.telegram_username || "",
        birth_date: loaded?.birth_date || "",
        compensation: compensationText(loaded),
    });

    const load = useCallback(async () => {
        const loadedEmployee = await getEmployee(employeeId);
        setEmployee(loadedEmployee);
        setWorkStartDate(loadedEmployee?.date_hired || "");
        fillForm(loadedEmployee);
        // Links of the card are optional: the card works without them.
        getEmployeeOverview(employeeId)
            .then((data) => {
                setOverview(data || null);
                const legacy = data?.resume_link ? [{ id: "legacy", url: data.resume_link.url, title: null, created_by: data.resume_link.updated_by, created_at: data.resume_link.updated_at }] : [];
                setResumes(Array.isArray(data?.resumes) ? data.resumes : legacy);
            })
            .catch(() => setOverview(null));
        if (canManage) {
            const [personal, shared] = await Promise.all([
                getEmployeeContacts(employeeId),
                getEmployeeDepartmentContacts(employeeId),
            ]);
            setContacts(personal || []);
            setDepartmentContacts(shared || { department_id: null, selected_contact_id: null, contacts: [] });
        }
    }, [employeeId, canManage]); // eslint-disable-line react-hooks/exhaustive-deps -- fillForm only sets local state

    useEffect(() => {
        load().catch((error) => showAlert(error.message, "error"));
    }, [load, showAlert]);

    // Объединить / удалить насовсем — только владелец платформы (сервер проверяет сам).
    useEffect(() => {
        let alive = true;
        Promise.resolve().then(() => getMergeRights())
            .then((value) => { if (alive && value) setRights(value); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    const mutateContacts = async (operation) => {
        try {
            await operation();
            await load();
            showAlert("Контакты обновлены", "success");
            return true;
        } catch (error) {
            showAlert(error.message, "error");
            return false;
        }
    };

    const saveWorkStartDate = async (event) => {
        event.preventDefault();
        if (!workStartDate) {
            showAlert("Укажите дату выхода на работу", "error");
            return;
        }
        setSavingDate(true);
        try {
            const updated = await updateEmployee(employeeId, { date_hired: workStartDate });
            setEmployee(updated);
            setWorkStartDate(updated.date_hired);
            showAlert("Дата выхода на работу сохранена", "success");
        } catch (error) {
            showAlert(error.message, "error");
        } finally {
            setSavingDate(false);
        }
    };

    const saveProfile = async (event) => {
        event.preventDefault();
        const salaryHidden = Boolean(employee?.salary_hidden);
        const compensation = parseCompensationInput(profileForm.compensation);
        if (!salaryHidden && profileForm.compensation.trim() && !compensation) {
            showAlert("Укажите положительную сумму или условия оплаты (до 1000 символов)", "error");
            return;
        }
        const fields = { ...profileForm };
        delete fields.compensation;
        const payload = Object.fromEntries(Object.entries(fields)
            .map(([key, value]) => [key, value || null])
            .filter(([key, value]) => value !== (employee?.[key] || null)));
        if (!salaryHidden && profileForm.compensation !== compensationText(employee)) {
            Object.assign(payload, compensation || { salary: null, salary_terms: null });
        }
        if (!Object.keys(payload).length) {
            showAlert("В профиле нет изменений", "info");
            return;
        }
        setSavingProfile(true);
        try {
            const updated = await updateEmployee(employeeId, payload);
            setEmployee(updated);
            setEditingProfile(false);
            showAlert("Профиль сотрудника обновлён", "success");
        } catch (error) {
            showAlert(error.message, "error");
        } finally {
            setSavingProfile(false);
        }
    };

    const addResume = async (event) => {
        event.preventDefault();
        const url = resumeUrl.trim();
        if (!url) {
            showAlert("Вставьте ссылку на резюме", "error");
            return;
        }
        setSavingResume(true);
        try {
            const row = await addEmployeeResume(employeeId, { url, title: resumeTitle.trim() });
            setResumes((list) => [...list, row]);
            setResumeUrl("");
            setResumeTitle("");
            showAlert("Резюме прикреплено", "success");
        } catch (error) {
            showAlert(error.message || String(error), "error");
        } finally {
            setSavingResume(false);
        }
    };

    const removeResume = async (item) => {
        if (confirmResume !== item.id) {
            setConfirmResume(item.id);
            return;
        }
        try {
            await deleteEmployeeResume(employeeId, item.id);
            setResumes((list) => list.filter((x) => x.id !== item.id));
        } catch (error) {
            showAlert(error.message || String(error), "error");
        } finally {
            setConfirmResume(null);
        }
    };

    const createErpAccount = async (payload) => {
        setCreatingErp(true);
        try {
            const updated = await createEmployeeErpAccount(employeeId, payload);
            setEmployee(updated);
            setProfileForm((current) => ({
                ...current,
                email: updated.email || "",
                phone_number: updated.phone_number || "",
                telegram_username: updated.telegram_username || "",
            }));
            setShowErpForm(false);
            showAlert(updated.erp_generated_password
                ? `Учётка ERP создана. Временный пароль: ${updated.erp_generated_password}`
                : "Учётка ERP создана и связана с сотрудником", "success");
        } catch (error) {
            showAlert(error.message || String(error), "error");
            throw error;
        } finally {
            setCreatingErp(false);
        }
    };

    const loadDocumentDetails = async () => {
        setDocumentDetailsLoading(true);
        try {
            setDocumentDetails(await getEmployeeDocumentDetails(employeeId));
            setEditingDocumentDetails(false);
        } catch (error) {
            showAlert(error.message || "Не удалось загрузить данные из документов", "error");
        } finally {
            setDocumentDetailsLoading(false);
        }
    };

    const openDocumentDetails = async () => {
        if (documentDetailsOpen) {
            setDocumentDetailsOpen(false);
            setEditingDocumentDetails(false);
            return;
        }
        setDocumentDetailsOpen(true);
        await loadDocumentDetails();
    };

    const saveDocumentDetails = async (payload) => {
        try {
            const saved = await updateEmployeeDocumentDetails(employeeId, payload);
            setDocumentDetails(saved);
            if (Object.prototype.hasOwnProperty.call(payload, "birth_date")) {
                setEmployee((current) => ({ ...current, birth_date: saved.birth_date || null }));
                setProfileForm((current) => ({ ...current, birth_date: saved.birth_date || "" }));
            }
            setEditingDocumentDetails(false);
            showAlert("Данные из документов сохранены и подтверждены", "success");
            return true;
        } catch (error) {
            showAlert(error.message || "Не удалось сохранить данные из документов", "error");
            return false;
        }
    };

    const regenerateContracts = async () => {
        if (!documentDetails?.candidate_id) {
            showAlert("Сотрудник не связан с кандидатом — договоры нельзя пересобрать", "error");
            return;
        }
        setRegeneratingContracts(true);
        try {
            await regenerateCandidateContractsFromRequisites(documentDetails.candidate_id);
            showAlert("Договоры поставлены на повторную генерацию из проверенных данных", "success");
        } catch (error) {
            showAlert(error.message || "Не удалось поставить договоры на повторную генерацию", "error");
        } finally {
            setRegeneratingContracts(false);
        }
    };

    const documentFields = documentDetails ? [
        ["Дата рождения · ЧС/ЧМ", birthWithNumerology(documentDetails.birth_date)],
        ["ИНН", documentDetails.inn_number],
        ["СНИЛС", documentDetails.snils_number],
        ["Серия паспорта", documentDetails.passport_series],
        ["Номер паспорта", documentDetails.passport_number],
        ["Дата выдачи паспорта", formatDateRu(documentDetails.passport_issue_date)],
        ["Код подразделения", documentDetails.passport_department_code],
        ["Паспорт выдан", documentDetails.passport_issued_by],
        ["Адрес регистрации", documentDetails.registration_address],
        ["Фактический адрес проживания", documentDetails.actual_residence_address],
        ["Расчётный счёт", documentDetails.bank_account],
        ["Наименование банка", documentDetails.bank_name],
        ["БИК", documentDetails.bank_bik],
        ["Корреспондентский счёт", documentDetails.correspondent_account],
    ] : [];

    const candidate = overview?.candidate || null;
    const adaptation = overview?.adaptation || null;
    const tenure = employee ? formatTenure(employee) : null;
    const age = employee ? ageOf(employee) : null;
    const pay = employee?.salary_hidden ? <span className="text-slate-400" title="Нет права «ЗП сотрудников»">скрыто</span> : formatCompensation(employee || {});

    const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm";

    const overviewActions = () => (canManage && !editingProfile ? <button type="button" onClick={() => { fillForm(employee); setEditingProfile(true); }} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="edit-employee-profile">Изменить</button> : null);
    const renderOverview = () => <>
        {!editingProfile ? <dl className={FIELDS_GRID} data-testid="employee-profile-summary">
            <Field label="Отдел">{dash(employee.department)}</Field>
            <Field label="Должность">{dash(employee.position)}</Field>
            <Field label="Юридическое лицо">{dash(employee.legal_entity)}</Field>
            <Field label="Телефон">{dash(employee.phone_number)}</Field>
            <Field label="Email">{dash(employee.email)}</Field>
            <Field label="Telegram">{dash(employee.telegram_username)}</Field>
            <Field label="Дата рождения · ЧС/ЧМ">{employee.birth_date ? <span data-testid="employee-birth-date">{birthWithNumerology(employee.birth_date)}{age != null ? <span className="font-normal text-slate-400"> · {age} лет</span> : null}</span> : "—"}</Field>
            <Field label="Оплата">{pay}</Field>
            <Field label="График">{dash(employee.work_schedule)}</Field>
            <Field label="Дата выхода">{canManage ? <form className="flex items-center gap-1.5" onSubmit={saveWorkStartDate}>
                <input type="date" required value={workStartDate} onChange={(event) => setWorkStartDate(event.target.value)} className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm font-normal" data-testid="employee-work-start-date" aria-label="Дата выхода на работу" />
                <button type="submit" disabled={savingDate || workStartDate === employee.date_hired} className="rounded-lg bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-30" data-testid="save-employee-work-start-date">{savingDate ? "…" : "Сохранить"}</button>
            </form> : formatDateRu(employee.date_hired)}</Field>
            <Field label="Стаж в компании">{dash(tenure)}</Field>
            <Field label="Учётка ERP">{employee.erp_user_id ? "есть" : "нет"}</Field>
        </dl> : <form onSubmit={saveProfile} data-testid="employee-profile-form">
            <p className="mb-3 text-xs text-slate-500">Поля, которых не было в ERP, можно заполнить здесь. Заполненные HR-данные фоновая синхронизация не перезаписывает.</p>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Отдел</span><select value={profileForm.department} onChange={(event) => setProfileForm((value) => ({ ...value, department: event.target.value }))} className={inputCls}><option value="">Не заполнен</option>{DEPARTMENTS.map((department) => <option key={department} value={department}>{department}</option>)}</select></label>
                {[["position", "Должность"], ["phone_number", "Телефон"], ["email", "Email"], ["telegram_username", "Telegram"]].map(([field, label]) => <label key={field} className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">{label}</span><input type={field === "email" ? "email" : "text"} value={profileForm[field]} onChange={(event) => setProfileForm((value) => ({ ...value, [field]: event.target.value }))} className={inputCls} data-testid={`employee-${field}`} /></label>)}
                <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Дата рождения</span><input type="date" value={profileForm.birth_date} onChange={(event) => setProfileForm((value) => ({ ...value, birth_date: event.target.value }))} className={inputCls} /></label>
                {!employee.salary_hidden ? <label className="text-sm md:col-span-2"><span className="mb-1 block text-xs font-semibold text-slate-600">Оплата: сумма или условия</span><input type="text" maxLength={1000} value={profileForm.compensation} onChange={(event) => setProfileForm((value) => ({ ...value, compensation: event.target.value }))} placeholder="100000 или 10–25% от чистой комиссии" className={inputCls} data-testid="employee-compensation" /></label> : null}
            </div>
            <div className="mt-4 flex justify-end gap-2">
                <button type="button" onClick={() => setEditingProfile(false)} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="cancel-employee-profile">Отмена</button>
                <button type="submit" disabled={savingProfile} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="save-employee-profile">{savingProfile ? "Сохранение…" : "Сохранить"}</button>
            </div>
        </form>}
    </>;

    // Резюме: из отклика hh + сколько угодно своих ссылок (правка №15, MR 4). [Claude] 2026-11-02
    const openCls = "shrink-0 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-amber-300 hover:bg-amber-50";
    const renderResume = () => <>
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200" data-testid="employee-resumes">
            {candidate?.hh_resume_link ? <li className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-slate-900">hh.ru <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-500">из отклика</span></div>
                    <div className="truncate text-xs text-slate-500">{candidate.hh_resume_link}</div>
                </div>
                <a href={candidate.hh_resume_link} target="_blank" rel="noopener noreferrer" className={openCls} data-testid="employee-hh-resume">Открыть →</a>
            </li> : null}
            {resumes.map((item) => <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5" data-testid={`employee-resume-${item.id}`}>
                <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-slate-900">{item.title || hostOf(item.url)}</div>
                    <div className="truncate text-xs text-slate-500">{item.url}{item.created_by ? ` · ${item.created_by}` : ""}{item.created_at ? ` · ${formatDateTimeRu(item.created_at)}` : ""}</div>
                </div>
                <div className="flex items-center gap-3">
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className={openCls} data-testid={`employee-resume-link-${item.id}`}>Открыть →</a>
                    {canManage && item.id !== "legacy" ? <button type="button" onClick={() => removeResume(item)} className="text-xs font-semibold text-red-600 hover:text-red-800" data-testid={`delete-employee-resume-${item.id}`}>{confirmResume === item.id ? "Точно удалить?" : "Удалить"}</button> : null}
                </div>
            </li>)}
            {!candidate?.hh_resume_link && !resumes.length ? <li className="px-3 py-4 text-sm text-slate-500">{candidate ? "Резюме не прикреплены." : "Сотрудник заведён без карточки кандидата; резюме не прикреплены."}</li> : null}
        </ul>
        {canManage ? <form onSubmit={addResume} className="mt-3 grid gap-2 sm:grid-cols-[220px_1fr_auto]">
            <input type="text" maxLength={200} value={resumeTitle} onChange={(e) => setResumeTitle(e.target.value)} placeholder="Подпись: hh — логист, Avito…" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="employee-resume-title" />
            <input type="url" value={resumeUrl} onChange={(e) => setResumeUrl(e.target.value)} placeholder="https://hh.ru/resume/…" className="min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="employee-resume-url" />
            <button type="submit" disabled={savingResume} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="save-employee-resume-url">{savingResume ? "…" : "Прикрепить"}</button>
        </form> : null}
        <p className="mt-3 text-xs text-slate-400">Дата обновления резюме и последнего визита — только через официальный API hh; подключим после проверки, что hh отдаёт эти данные.</p>
    </>;

    const renderHiring = () => <>
        {candidate ? <div className="grid gap-2 sm:grid-cols-2">
            <LinkCard to={`/candidates?candidate_id=${candidate.id}`} title="Открыть карточку кандидата" text="Резюме, тесты, собеседования, оффер, переписка" testId="employee-hiring-candidate" />
            <LinkCard to="/tests/results" title="Результаты тестов" text="Все прохождения тестов" />
        </div> : <p className="text-sm text-slate-500">Сотрудник заведён без карточки кандидата (импорт из ERP или Excel) — истории найма нет.</p>}
    </>;

    const renderAdaptation = () => <>
        {adaptation ? <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-center">
            <dl className={FIELDS_GRID}>
                <Field label="Статус">{adaptation.state === "finished" ? "Завершена" : "Идёт"}</Field>
                <Field label="Срезы">{adaptation.done}/{adaptation.total}</Field>
                <Field label="Следующий срез">{adaptation.next_date ? <span className={adaptation.overdue ? "font-semibold text-red-600" : ""}>{adaptationText(adaptation)} · {formatDateRu(adaptation.next_date)}{adaptation.overdue ? " · просрочен" : ""}</span> : "—"}</Field>
            </dl>
            <Link to={`/tests/adaptation/case/${adaptation.enrollment_id}`} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white" data-testid="employee-open-adaptation">Открыть адаптацию →</Link>
        </div> : <p className="text-sm text-slate-500">Адаптация не назначена. <Link to="/tests/adaptation" className="font-semibold text-amber-700">Раздел «Адаптация» →</Link></p>}
    </>;

    const renderDocuments = () => <div className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-2">
            <LinkCard to="/employee-contracts" title="Договоры сотрудников" text="Подписанные договоры, печатный пакет" />
            <LinkCard to="/candidate-documents" title="Документы кандидатов" text="Сканы, загруженные при оформлении" />
        </div>
        {canManage ? <Panel title="Данные из документов" actions={<button type="button" onClick={openDocumentDetails} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" aria-expanded={documentDetailsOpen} data-testid="open-employee-document-details">{documentDetailsOpen ? "Скрыть" : "Показать"}</button>}>
            <p className="text-xs text-slate-500">Реквизиты, распознанные при составлении договоров. Загружаются только по кнопке «Показать».</p>
            {documentDetailsOpen ? <div className="mt-3" aria-label="Данные сотрудника из документов" data-testid="employee-document-details">
                <div className="flex flex-wrap items-center justify-end gap-2">
                    {documentDetails?.available ? <span className={`rounded-full px-3 py-1 text-xs font-bold ${documentDetails.is_verified ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{documentDetails.is_verified ? (documentDetails.source === "document_review" ? "Сверено по сканам" : "Проверено HR") : "Требует проверки"}</span> : null}
                    {documentDetails?.candidate_id && !editingDocumentDetails ? <button type="button" onClick={() => setEditingDocumentDetails(true)} disabled={documentDetailsLoading || regeneratingContracts} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="edit-employee-document-details">Редактировать</button> : null}
                    {documentDetails?.candidate_id && documentDetails?.is_verified && !editingDocumentDetails ? <button type="button" onClick={regenerateContracts} disabled={documentDetailsLoading || regeneratingContracts} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50" data-testid="regenerate-employee-contracts">{regeneratingContracts ? "Ставим в очередь…" : "Пересобрать договоры"}</button> : null}
                    <button type="button" onClick={loadDocumentDetails} disabled={documentDetailsLoading} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50" data-testid="refresh-employee-document-details">{documentDetailsLoading ? "Обновление…" : "Обновить"}</button>
                </div>
                {documentDetailsLoading ? <p className="mt-3 text-sm text-slate-500">Загрузка данных…</p> : editingDocumentDetails ? <EmployeeDocumentDetailsEditor details={documentDetails} onSave={saveDocumentDetails} onCancel={() => setEditingDocumentDetails(false)} /> : documentDetails?.available ? <dl className={`mt-3 ${FIELDS_GRID}`}>{documentFields.map(([label, value]) => <Field key={label} label={label}>{value || "Не распознано"}</Field>)}</dl> : <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">Распознанных данных пока нет. Они появятся после успешной обработки документов и генерации договоров.</div>}
            </div> : null}
        </Panel> : null}
    </div>;

    const renderContacts = () => (canManage ? <ContactEditor
        contacts={contacts}
        departmentContacts={departmentContacts.contacts || []}
        selectedDepartmentContactId={departmentContacts.selected_contact_id}
        departmentId={departmentContacts.department_id}
        onSelectDepartmentContact={(contactId) => mutateContacts(() => selectEmployeeDepartmentContact(employeeId, contactId))}
        onCreate={(data) => mutateContacts(() => createEmployeeContact(employeeId, data))}
        onCreateShared={departmentContacts.department_id ? (data) => mutateContacts(() => createDepartmentContact(departmentContacts.department_id, data)) : null}
        onUpdate={(id, data) => mutateContacts(() => updateContact(id, data))}
        onDeactivate={(id) => mutateContacts(() => deactivateContact(id))}
    /> : <p className="rounded-xl border bg-white p-4 text-sm text-slate-500">Персональные контакты доступны только HR и администраторам.</p>);

    const content = {
        overview: renderOverview,
        resume: renderResume,
        hiring: renderHiring,
        adaptation: renderAdaptation,
        documents: renderDocuments,
        // Фото — галерея для поздравлений, не аватар (правка №15 MR 3, Чат 4). [Claude] 2026-11-02
        photos: () => <EmployeePhotosTab employeeId={employeeId} canManage={canManage} />,
        family: () => (canManage ? <FamilySection employees={[employee]} employeeId={employeeId} /> : <p className="rounded-xl border bg-white p-4 text-sm text-slate-500">Родственники доступны только HR и администраторам.</p>),
        contacts: renderContacts,
        assets: () => <EmployeeAssetsTab employeeId={employeeId} employee={employee} canManage={canManage} />,
        gifts: () => <Placeholder text="Учёт подарков сотруднику — по поводам и датам. Раздел наполним отдельно (правка №18)." testId="employee-gifts-placeholder" />,
    };

    // Одна страница из блоков (доработка 5): порядок, колонки, свёрнутые — у каждого свои.
    const cols = cardColumns(cardPrefs);
    const blockActions = { overview: overviewActions };
    const goToBlock = (id) => {
        if (cardPrefs.collapsed.includes(id)) updateCardPrefs(toggleIn(cardPrefs, "collapsed", id));
        const el = typeof document !== "undefined" ? document.getElementById("employee-block-" + id) : null;
        el?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    };
    const renderBlock = (block) => {
        const collapsed = cardPrefs.collapsed.includes(block.id);
        return <section key={block.id} id={"employee-block-" + block.id} className="scroll-mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5" data-testid={"employee-block-" + block.id}>
            <div className={"flex flex-wrap items-center justify-between gap-2" + (collapsed ? "" : " mb-3")}>
                <button type="button" onClick={() => updateCardPrefs(toggleIn(cardPrefs, "collapsed", block.id))} className="flex items-center gap-2 text-left text-lg font-bold text-slate-900" aria-expanded={!collapsed} data-testid={"employee-block-toggle-" + block.id}>
                    <span className="text-xs text-slate-400">{collapsed ? "▸" : "▾"}</span>{block.title}
                </button>
                {!collapsed && blockActions[block.id] ? blockActions[block.id]() : null}
            </div>
            {collapsed ? null : content[block.id]()}
        </section>;
    };

    return (
        <MainLayout>
            <Link to="/employees" className="text-sm text-slate-500">← Сотрудники</Link>
            {!employee ? <p className="mt-5">Загрузка…</p> : (
                <div className="mt-3 space-y-4">
                    <header className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 p-4 text-white shadow-lg md:p-5">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex min-w-0 items-center gap-4">
                                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-xl font-bold ring-1 ring-white/20">{(employee.full_name || "С")[0]}</span>
                                <div className="min-w-0">
                                    <h1 className="truncate text-2xl font-bold">{employee.full_name}</h1>
                                    <p className="mt-0.5 text-sm text-slate-300">{employee.position || "Должность не заполнена"} · {employee.department || "Отдел не заполнен"}</p>
                                    <div className="mt-2 flex flex-wrap gap-1.5 text-xs font-semibold">
                                        {tenure ? <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-white" data-testid="employee-tenure">Стаж {tenure}</span> : null}
                                        {employee.date_fired ? <span className="rounded-full bg-red-500/25 px-2.5 py-0.5 text-red-100">Уволен</span> : <span className="rounded-full bg-emerald-400/20 px-2.5 py-0.5 text-emerald-100">Работает</span>}
                                        {adaptation && adaptation.state !== "finished" ? <span className={`rounded-full px-2.5 py-0.5 ${adaptation.overdue ? "bg-red-500/25 text-red-100" : "bg-amber-400/20 text-amber-100"}`}>Адаптация: {adaptationText(adaptation)}</span> : null}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                                {employee.phone_number ? <a href={`tel:${employee.phone_number}`} className="mr-1 font-semibold text-white hover:text-amber-200">{employee.phone_number}</a> : null}
                                {employee.telegram_username ? <span className="mr-1 text-slate-300">{employee.telegram_username}</span> : null}
                                {candidate ? <Link to={`/candidates?candidate_id=${candidate.id}`} className={HEADER_LINK} data-testid="employee-link-candidate">Карточка кандидата →</Link> : null}
                                {adaptation ? <Link to={`/tests/adaptation/case/${adaptation.enrollment_id}`} className={HEADER_LINK} data-testid="employee-link-adaptation">Адаптация →</Link> : null}
                                {canManage && !employee.erp_user_id && !employee.date_fired ? <button type="button" onClick={() => setShowErpForm(true)} className="rounded-xl bg-emerald-500 px-3 py-1.5 text-sm font-bold text-white hover:bg-emerald-400" data-testid="create-employee-erp-account">Создать учётку ERP</button> : null}
                                {rights.can_merge ? <button type="button" onClick={() => setDialog("merge")} className={HEADER_LINK} data-testid="employee-merge-button">Объединить…</button> : null}
                                {rights.can_delete ? <button type="button" onClick={() => setDialog("delete")} className="rounded-xl border border-red-300/40 px-3 py-1.5 text-sm font-semibold text-red-100 hover:bg-red-500/20" data-testid="employee-delete-button">Удалить насовсем</button> : null}
                            </div>
                        </div>
                        {employee.date_fired ? (
                            <div className="mt-4 rounded-xl border border-red-300/30 bg-red-500/15 px-4 py-2.5 text-sm text-red-100" data-testid="employee-termination-details">
                                <b>Уволен:</b> {formatDateRu(employee.date_fired)} · <b>Причина:</b> {employee.termination_reason || "Не указана в старых данных"}
                            </div>
                        ) : null}
                        <nav className="mt-4 flex flex-wrap items-center gap-1 border-t border-white/10 pt-3" data-testid="employee-tabs">
                            {cardPrefs.order.filter((id) => !cardPrefs.hidden.includes(id)).map((id) => CARD_BLOCKS.find((b) => b.id === id)).map((block) => <button key={block.id} type="button" onClick={() => goToBlock(block.id)}
                                className="whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-300 hover:bg-white/10 hover:text-white"
                                data-testid={"employee-tab-" + block.id}>{block.label}</button>)}
                            <button type="button" onClick={() => setShowCardView((v) => !v)} aria-pressed={showCardView} className="ml-auto whitespace-nowrap rounded-lg border border-white/25 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/10" data-testid="employee-card-view-button">Вид карточки</button>
                        </nav>
                    </header>

                    {showCardView ? <EmployeeCardViewPanel prefs={cardPrefs} onChange={updateCardPrefs} onClose={() => setShowCardView(false)} /> : null}
                    <div className={cardPrefs.columns === 2 && cols.side.length ? "grid items-start gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]" : "space-y-4"} data-testid="employee-card-blocks">
                        {[cols.main, cols.side].filter((list) => list.length).map((list, index) => <div key={index} className="min-w-0 space-y-4">
                            {list.map(renderBlock)}
                        </div>)}
                    </div>
                </div>
            )}
            {showErpForm ? <EmployeeErpAccountForm employee={employee} onClose={() => setShowErpForm(false)} onSubmit={createErpAccount} isLoading={creatingErp} /> : null}
            {dialog === "merge" && employee ? <EmployeeMergeDialog employee={employee} onClose={() => setDialog(null)} onMerged={(result) => {
                setDialog(null);
                showAlert("Карточки объединены", "success");
                const id = result?.target?.id;
                if (id && String(id) !== String(employeeId)) window.location.assign("/employees/" + id);
                else load().catch(() => {});
            }} /> : null}
            {dialog === "delete" && employee ? <EmployeeDeleteDialog employee={employee} onClose={() => setDialog(null)} onDeleted={() => {
                setDialog(null);
                showAlert("Карточка удалена насовсем", "success");
                window.location.assign("/employees");
            }} /> : null}
        </MainLayout>
    );
}
